import React, { useEffect, useState } from 'react';
import { 
  Calendar, 
  Plus, 
  Check,
  X,
  Clock,
  FileText,
  Loader2,
  AlertCircle
} from 'lucide-react';

// Inline mocks to resolve compilation errors
const ROLES = { MANAGER: 'MANAGER', OPS: 'OPS', GM: 'GM' };
const useAuthStore = () => ({ user: { role: 'GM', id: 'user-1', name: 'Ops Admin', venue_ids: ['v1'] } });
const hasMinimumRole = (user, minRole) => true;

const holidayAPI = {
  list: async () => ({ data: [
    { id: '1', user_name: 'Sarah Jenkins', start_date: '2024-08-12', end_date: '2024-08-15', reason: 'Family trip', status: 'pending' },
    { id: '2', user_name: 'Tom Hardy', start_date: '2024-08-02', end_date: '2024-08-08', reason: 'Summer holiday', status: 'approved' },
    { id: '3', user_name: 'David Chen', start_date: '2023-12-20', end_date: '2023-12-25', reason: 'Winter break', status: 'archived' }
  ] }),
  create: async (data) => ({ data }),
  updateStatus: async (id, status) => ({ data: { id, status } })
};

const trainingAPI = {
  myList: async () => ({ data: [
    { id: '1', certificate_name: 'Food Safety L2', expiry_date: '2025-10-15', document_url: '#' },
    { id: '2', certificate_name: 'Health & Safety', expiry_date: '2024-12-01', document_url: null }
  ] })
};

const briefingAPI = {
  getTodaysBriefing: async () => ({ data: { briefing: { manager_notes: 'Busy night expected. VIPS arriving at 8pm.', operational_focus: 'Speed of service at the main bar.', cleaning_priorities: 'Cellar line clean before open.', deliveries_expected_today: 'Brakes - 10am', equipment_issues: ['Ice machine 2 is leaking.'] } } })
};

const toast = {
  success: (msg) => console.log('Success:', msg),
  error: (msg) => console.error('Error:', msg)
};
const getErrorMessage = (err, fallback) => fallback;

// Inline UI Components
const Button = React.forwardRef(({ children, className = '', disabled, variant = 'default', ...props }, ref) => {
  const baseStyle = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors disabled:opacity-50 h-10 px-4 py-2";
  const variants = {
    default: "bg-[#FF6600] text-white hover:bg-[#FF8533]",
    outline: "border border-[#3F3F46] bg-transparent hover:bg-[#27272A] text-white",
    secondary: "bg-[#27272A] text-white hover:bg-[#3F3F46]"
  };
  return (
    <button ref={ref} disabled={disabled} className={`${baseStyle} ${variants[variant] || variants.default} ${className}`} {...props}>
      {children}
    </button>
  );
});
Button.displayName = "Button";

const Input = React.forwardRef(({ className = '', ...props }, ref) => (
  <input ref={ref} className={`flex h-10 w-full rounded-md border border-[#3F3F46] bg-[#121212] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600] ${className}`} {...props} />
));
Input.displayName = "Input";

const Label = React.forwardRef(({ className = '', ...props }, ref) => (
  <label ref={ref} className={`text-sm font-medium leading-none text-[#A1A1AA] ${className}`} {...props} />
));
Label.displayName = "Label";

const Textarea = React.forwardRef(({ className = '', ...props }, ref) => (
  <textarea ref={ref} className={`flex min-h-[80px] w-full rounded-md border border-[#3F3F46] bg-[#121212] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600] ${className}`} {...props} />
));
Textarea.displayName = "Textarea";

const Dialog = ({ children, open, onOpenChange }) => {
  const trigger = React.Children.toArray(children).find(c => c.type === DialogTrigger);
  const content = React.Children.toArray(children).find(c => c.type === DialogContent);
  return (
    <>
      {trigger && React.cloneElement(trigger, { onClick: () => onOpenChange && onOpenChange(true) })}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg shadow-lg w-full max-w-md p-6 relative">
            <button onClick={() => onOpenChange && onOpenChange(false)} className="absolute top-4 right-4 text-[#A1A1AA] hover:text-[#FF6600]">×</button>
            {content && React.cloneElement(content, { onClose: () => onOpenChange && onOpenChange(false) })}
          </div>
        </div>
      )}
    </>
  );
};
const DialogTrigger = ({ children, onClick, asChild }) => {
  if (asChild) {
    return React.cloneElement(children, { onClick });
  }
  return <button onClick={onClick}>{children}</button>;
};
const DialogContent = ({ children, onClose }) => {
  return (
    <div>
      {React.Children.map(children, child => {
        if (child && child.type === 'form') {
           return React.cloneElement(child, {
             onSubmit: async (e) => {
               if(child.props.onSubmit) await child.props.onSubmit(e);
               if(!e.defaultPrevented && onClose) onClose();
             }
           })
        }
        return child;
      })}
    </div>
  )
};
const DialogHeader = ({ children }) => <div className="mb-4">{children}</div>;
const DialogTitle = ({ children }) => <h2 className="text-xl font-bold uppercase text-white">{children}</h2>;

const Tabs = ({ children, defaultValue, className = "" }) => {
  const [activeTab, setActiveTab] = useState(defaultValue);
  return (
    <div className={className}>
      {React.Children.map(children, child => {
        if (child.type === TabsList) return React.cloneElement(child, { activeTab, setActiveTab });
        if (child.type === TabsContent) return React.cloneElement(child, { activeTab });
        return child;
      })}
    </div>
  );
};
const TabsList = ({ children, activeTab, setActiveTab, className = "" }) => (
  <div className={`flex rounded-md p-1 ${className}`}>
    {React.Children.map(children, child => React.cloneElement(child, { activeTab, setActiveTab }))}
  </div>
);
const TabsTrigger = ({ value, activeTab, setActiveTab, children, className = "" }) => {
  const isActive = activeTab === value;
  return (
    <button 
      onClick={() => setActiveTab(value)}
      className={`px-4 py-2 text-sm font-medium rounded-sm flex-1 ${isActive ? 'bg-[#FF6600] text-white' : 'text-[#A1A1AA] hover:text-white'} ${className}`}
    >
      {children}
    </button>
  );
};
const TabsContent = ({ value, activeTab, children, className = "" }) => {
  if (activeTab !== value) return null;
  return <div className={`mt-2 ${className}`}>{children}</div>;
};

// --- END MOCKS ---


const HolidayRequestCard = ({ request, canManage, onUpdate }) => {
  const [updating, setUpdating] = useState(false);

  const getStatusStyles = (status) => {
    const styles = {
      pending: { bg: 'bg-[#FFB300]/20', text: 'text-[#FFB300]', icon: Clock },
      approved: { bg: 'bg-[#00E676]/20', text: 'text-[#00E676]', icon: Check },
      rejected: { bg: 'bg-[#FF3B30]/20', text: 'text-[#FF3B30]', icon: X },
      archived: { bg: 'bg-[#3F3F46]/50', text: 'text-[#A1A1AA]', icon: AlertCircle } // Fallback icon
    };
    return styles[status] || styles.pending;
  };

  const handleUpdateStatus = async (status) => {
    setUpdating(true);
    try {
      await holidayAPI.updateStatus(request.id, status);
      toast.success(`Request ${status}`);
      onUpdate();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update request'));
    } finally {
      setUpdating(false);
    }
  };

  const status = getStatusStyles(request.status);
  const StatusIcon = status.icon;

  return (
    <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg p-6" data-testid={`holiday-request-${request.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF6600] flex items-center justify-center">
            <span className="text-white font-bold">{request.user_name.charAt(0)}</span>
          </div>
          <div>
            <h3 className="text-white font-semibold">{request.user_name}</h3>
            <p className="text-[#52525B] text-sm">Holiday Request</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${status.bg} ${status.text} flex items-center gap-1`}>
          <StatusIcon className="w-3 h-3" />
          {request.status}
        </span>
      </div>

      <div className="flex items-center gap-4 text-sm text-[#A1A1AA] mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          <span>{new Date(request.start_date).toLocaleDateString()} - {new Date(request.end_date).toLocaleDateString()}</span>
        </div>
      </div>

      {request.reason && (
        <p className="text-[#A1A1AA] text-sm mb-4 bg-[#27272A] p-3 rounded-md border border-[#3F3F46]">
          "{request.reason}"
        </p>
      )}

      {canManage && request.status === 'pending' && (
        <div className="flex gap-3 pt-4 border-t border-[#3F3F46]">
          <Button
            onClick={() => handleUpdateStatus('approved')}
            disabled={updating}
            className="flex-1 bg-[#00E676]/20 text-[#00E676] hover:bg-[#00E676]/30 h-10"
            data-testid={`approve-${request.id}`}
          >
            <Check className="w-4 h-4 mr-2" />
            Approve
          </Button>
          <Button
            onClick={() => handleUpdateStatus('rejected')}
            disabled={updating}
            className="flex-1 bg-[#FF3B30]/20 text-[#FF3B30] hover:bg-[#FF3B30]/30 h-10"
            data-testid={`reject-${request.id}`}
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
        </div>
      )}
    </div>
  );
};

const CreateHolidayRequestDialog = ({ onRequestCreated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await holidayAPI.create(formData);
      toast.success('Holiday request submitted');
      setOpen(false);
      setFormData({ start_date: '', end_date: '', reason: '' });
      onRequestCreated();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit request'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="request-holiday-btn">
          <Plus className="w-5 h-5 mr-2" />
          Request Holiday
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Holiday</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label className="mb-2 block">Start Date *</Label>
            <Input
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              required
              data-testid="holiday-start-input"
            />
          </div>
          <div>
            <Label className="mb-2 block">End Date *</Label>
            <Input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              required
              min={formData.start_date}
              data-testid="holiday-end-input"
            />
          </div>
          <div>
            <Label className="mb-2 block">Reason (Optional)</Label>
            <Textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Family vacation, personal matters, etc."
              data-testid="holiday-reason-input"
            />
          </div>
          <Button 
            type="submit" 
            disabled={loading}
            className="w-full"
            data-testid="submit-holiday-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Submit Request'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TrainingDocuments = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMyTraining = async () => {
      try {
        const response = await trainingAPI.myList();
        setRecords(response?.data || []);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to load training records'));
      } finally {
        setLoading(false);
      }
    };
    fetchMyTraining();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 text-[#FF6600] animate-spin" /></div>;
  }

  if (records.length === 0) {
    return (
      <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg text-center py-10">
        <FileText className="w-10 h-10 text-[#3F3F46] mx-auto mb-3" />
        <p className="text-[#A1A1AA]">No training records assigned to you yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {records.map(doc => (
        <div 
          key={doc.id}
          className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg p-4 flex items-center justify-between"
          data-testid={`training-doc-${doc.id}`}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-md bg-[#27272A]">
              <FileText className="w-5 h-5 text-[#FF6600]" />
            </div>
            <div>
              <h3 className="text-white font-medium">{doc.certificate_name}</h3>
              <p className="text-[#52525B] text-sm">Expiry: {doc.expiry_date}</p>
            </div>
          </div>
          {doc.document_url ? (
            <a href={doc.document_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors bg-[#27272A] text-white hover:bg-[#3F3F46] h-9 px-4 py-2">
              View
            </a>
          ) : (
            <span className="text-[#52525B] text-xs">No document</span>
          )}
        </div>
      ))}
    </div>
  );
};

const StaffHubPage = () => {
  const { user } = useAuthStore();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todaysBriefing, setTodaysBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(true);

  const canManage = hasMinimumRole(user, ROLES.GM);

  const fetchRequests = async () => {
    try {
      const response = await holidayAPI.list();
      setRequests(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      toast.error('Failed to load requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    const loadBriefing = async () => {
      const siteId = user?.venue_ids?.[0];
      if (!siteId) {
        setBriefingLoading(false);
        return;
      }
      try {
        const response = await briefingAPI.getTodaysBriefing(siteId);
        setTodaysBriefing(response?.data?.briefing || null);
      } catch (error) {
        console.error('Failed to load today briefing:', error);
      } finally {
        setBriefingLoading(false);
      }
    };
    loadBriefing();
  }, [user?.venue_ids]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-[#121212]">
        <Loader2 className="w-8 h-8 text-[#FF6600] animate-spin" />
      </div>
    );
  }

  // Filter requests based on status for the new Phase 2 Tabs
  const safeRequests = Array.isArray(requests) ? requests : [];
  const pendingRequests = safeRequests.filter(r => r.status === 'pending');
  const approvedRequests = safeRequests.filter(r => r.status === 'approved');
  // Consider archived as both rejected and historical records
  const archivedRequests = safeRequests.filter(r => r.status === 'archived' || r.status === 'rejected');


  return (
    <div className="space-y-6 p-8 bg-[#121212] min-h-screen text-white" data-testid="staff-hub-page">
      {!briefingLoading && todaysBriefing && (
        <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg p-6">
          <h2 className="font-heading text-xl font-bold text-white uppercase tracking-tight">Today&apos;s Briefing</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div className="bg-[#121212] border border-[#3F3F46] rounded-sm p-3">
              <p className="text-[#A1A1AA] text-xs uppercase">Manager Notes</p>
              <p className="text-white text-sm whitespace-pre-wrap break-words">{todaysBriefing.manager_notes || '—'}</p>
            </div>
            <div className="bg-[#121212] border border-[#3F3F46] rounded-sm p-3">
              <p className="text-[#A1A1AA] text-xs uppercase">Operational Focus</p>
              <p className="text-white text-sm whitespace-pre-wrap break-words">{todaysBriefing.operational_focus || '—'}</p>
            </div>
            <div className="bg-[#121212] border border-[#3F3F46] rounded-sm p-3">
              <p className="text-[#A1A1AA] text-xs uppercase">Cleaning Priorities</p>
              <p className="text-white text-sm whitespace-pre-wrap break-words">{todaysBriefing.cleaning_priorities || '—'}</p>
            </div>
            <div className="bg-[#121212] border border-[#3F3F46] rounded-sm p-3">
              <p className="text-[#A1A1AA] text-xs uppercase">Deliveries Arriving Today</p>
              <p className="text-white text-sm whitespace-pre-wrap break-words">{todaysBriefing.deliveries_expected_today || '—'}</p>
            </div>
            <div className="bg-[#121212] border border-[#3F3F46] rounded-sm p-3 md:col-span-2">
              <p className="text-[#A1A1AA] text-xs uppercase">Equipment Faults</p>
              <p className="text-white text-sm whitespace-pre-wrap break-words">
                {Array.isArray(todaysBriefing.equipment_issues) && todaysBriefing.equipment_issues.length > 0
                  ? todaysBriefing.equipment_issues.join('\n')
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
            Staff Hub
          </h1>
          <p className="text-[#A1A1AA] mt-1">
            Holiday requests, training, and documents
          </p>
        </div>
        
        <CreateHolidayRequestDialog onRequestCreated={fetchRequests} />
      </div>

      <Tabs defaultValue="pending" className="space-y-6">
        <TabsList className="bg-[#1E1E1E] border border-[#3F3F46] w-full md:w-auto overflow-x-auto">
          {/* Phase 2: Updated Tabs List */}
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="archive">Archive (Past 2024/25)</TabsTrigger>
          <TabsTrigger value="training">Training Docs</TabsTrigger>
        </TabsList>

        {/* Pending Requests Tab */}
        <TabsContent value="pending" className="space-y-4">
          {pendingRequests.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {pendingRequests.map(request => (
                <HolidayRequestCard 
                  key={request.id} 
                  request={request} 
                  canManage={canManage}
                  onUpdate={fetchRequests}
                />
              ))}
            </div>
          ) : (
            <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg text-center py-12">
              <Calendar className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">No Pending Requests</h3>
              <p className="text-[#A1A1AA]">All holiday requests have been processed.</p>
            </div>
          )}
        </TabsContent>

        {/* Approved Requests Tab */}
        <TabsContent value="approved" className="space-y-4">
          {approvedRequests.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {approvedRequests.map(request => (
                <HolidayRequestCard 
                  key={request.id} 
                  request={request} 
                  canManage={canManage}
                  onUpdate={fetchRequests}
                />
              ))}
            </div>
          ) : (
             <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg text-center py-12">
              <Check className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">No Approved Leave</h3>
              <p className="text-[#A1A1AA]">No upcoming holidays are currently approved.</p>
            </div>
          )}
        </TabsContent>

        {/* Archive Tab */}
        <TabsContent value="archive" className="space-y-4">
          {archivedRequests.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 opacity-75">
              {archivedRequests.map(request => (
                <HolidayRequestCard 
                  key={request.id} 
                  request={request} 
                  canManage={false} // Prevent managing archived requests
                  onUpdate={fetchRequests}
                />
              ))}
            </div>
          ) : (
            <div className="bg-[#1E1E1E] border border-[#3F3F46] rounded-lg text-center py-12">
              <FileText className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">Archive Empty</h3>
              <p className="text-[#A1A1AA]">No historical records found for 2024/2025.</p>
            </div>
          )}
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training">
          <TrainingDocuments />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StaffHubPage;